import { digitalTwinActionTypes } from '../actions/digitalTwin.types';
import { EDIT_MODES } from '../constants/editModes';
import { liveEventDedupKey } from '../utils/enrichActivityEvents';
import { apiToSceneState } from '../utils/sceneAdapters';

export const initialState = {
  scene: {
    machine: null,
    components: [],
    locations: [],
    conditions: { machine: null, components: {} },
  },
  loading: false,
  error: null,
  selectedItemId: null,
  editMode: EDIT_MODES.VIEW,
  liveEvents: [],
  // Identity of the request/machine the slice currently reflects. Responses
  // that do not match are dropped rather than applied — see isStale().
  machineId: null,
  sceneRequestId: 0,
  eventsRequestId: 0,
  // In-flight flag for the reorder PATCH so the drag surface can be disabled
  // and a second concurrent write refused.
  savingOrder: false,
};

const requestIdOf = action => (action.meta && action.meta.requestId) || 0;

/**
 * A response is stale if it belongs to a different machine than the one the
 * slice is now tracking, or if a newer request of the same kind has since been
 * issued. Both checks are needed: aborting cancels most in-flight requests, but
 * a response already on the wire cannot be recalled.
 */
function isStale(state, action, currentRequestId) {
  if (action.machineId != null
    && state.machineId != null
    && String(action.machineId) !== String(state.machineId)) {
    return true;
  }
  return requestIdOf(action) < currentRequestId;
}

export default function digitalTwinReducer(state = initialState, action) {
  switch (action.type) {
    case digitalTwinActionTypes.FETCH_SCENE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        machineId: action.machineId != null ? action.machineId : state.machineId,
        sceneRequestId: requestIdOf(action),
      };
    case digitalTwinActionTypes.FETCH_SCENE_SUCCESS:
      if (isStale(state, action, state.sceneRequestId)) return state;
      return {
        ...state,
        loading: false,
        error: null,
        // Delegate to apiToSceneState (same logic as the removed normalizeScene)
        scene: apiToSceneState(action.result),
      };
    case digitalTwinActionTypes.FETCH_SCENE_FAILURE:
      if (isStale(state, action, state.sceneRequestId)) return state;
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case digitalTwinActionTypes.FETCH_EVENTS_REQUEST:
      return { ...state, eventsRequestId: requestIdOf(action) };
    case digitalTwinActionTypes.REORDER_COMPONENTS_REQUEST:
      return { ...state, savingOrder: true, error: null };
    case digitalTwinActionTypes.REORDER_COMPONENTS_SUCCESS:
      return { ...state, savingOrder: false };
    case digitalTwinActionTypes.REORDER_COMPONENTS_FAILURE:
      return { ...state, savingOrder: false, error: action.error };
    case digitalTwinActionTypes.SET_SELECTED_ITEM:
      return {
        ...state,
        selectedItemId: action.selectedItemId,
      };
    case digitalTwinActionTypes.SET_EDIT_MODE:
      return {
        ...state,
        editMode: action.editMode,
      };
    case digitalTwinActionTypes.SET_INITIAL_EVENTS: {
      if (isStale(state, action, state.eventsRequestId)) return state;
      // No slice here. The API already bounds the payload via per_page, and it
      // appends events_under_observation *after* pagination (see
      // activity_controller.get_activities) — so those land at indices >= the
      // page size and a slice-to-page-size silently discarded exactly the
      // events an analyst is actively watching.
      const incoming = action.events || [];
      const existingIds = state.liveEvents.map(liveEventDedupKey).join('|');
      const incomingIds = incoming.map(liveEventDedupKey).join('|');
      if (existingIds === incomingIds && incoming.length === state.liveEvents.length) return state;
      return { ...state, liveEvents: incoming };
    }
    case digitalTwinActionTypes.CLEAR_STATE:
      // Clear the data, but ADVANCE the request watermarks rather than resetting
      // them. Returning `initialState` wholesale put machineId back to null and
      // both request ids back to 0, which disarmed isStale() entirely: the
      // machine check skips a null machineId and every real request id is >= 0,
      // so a response already on the wire when the view unmounted sailed past
      // both checks and repopulated the slice that had just been cleared —
      // exactly the failure the guard exists to prevent. Bumping the watermark
      // makes every request issued before the clear stale by definition.
      return {
        ...initialState,
        sceneRequestId: state.sceneRequestId + 1,
        eventsRequestId: state.eventsRequestId + 1,
      };
    default:
      return state;
  }
}
